import {
  Building2,
  Clock,
  Edit3,
  FileClock,
  PauseCircle,
  Play,
  ShieldCheck,
  Sparkles,
  UserCog,
  UserPlus,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import {
  Badge,
  Card,
  Input,
  Button as ShButton,
  Skeleton,
} from '../../components/shadcn/index.js';
import { EmptyState, FormField } from '../../components/ui/index.js';
import { formatDateTime } from '../../lib/intl.js';
import { platformApi } from '../../lib/platformApi.js';

import { AuditDiff } from './_shared/AuditDiff.js';

const PAGE_SIZE = 50;

interface AuditEntry {
  id: string;
  platformAdminId: string;
  action: string;
  targetTenantId: string | null;
  diff: unknown;
  createdAt: string;
}

interface AdminLite {
  id: string;
  email: string;
}

interface TenantLite {
  id: string;
  name: string;
  slug: string;
}

interface ActionMeta {
  icon: React.ComponentType<{ className?: string }>;
  /** Variant on the shadcn Badge for the action chip. */
  variant: 'default' | 'success' | 'warning' | 'danger' | 'info' | 'secondary' | 'outline';
}

const ACTION_META: Record<string, ActionMeta> = {
  'tenant.create': { icon: Building2, variant: 'success' },
  'tenant.update': { icon: Edit3, variant: 'info' },
  'tenant.suspend': { icon: PauseCircle, variant: 'warning' },
  'tenant.reactivate': { icon: Play, variant: 'success' },
  'tenant.impersonate': { icon: UserCog, variant: 'danger' },
  'admin.create': { icon: UserPlus, variant: 'success' },
  'admin.update': { icon: ShieldCheck, variant: 'info' },
  'admin.login': { icon: ShieldCheck, variant: 'secondary' },
};

function metaFor(action: string): ActionMeta {
  return ACTION_META[action] ?? { icon: Sparkles, variant: 'outline' };
}

type TFn = (key: string, opts?: { defaultValue?: string }) => string;

/**
 * Three states are visible to the operator:
 *   - directory still loading (null map) → render the raw UUID alone, no
 *     "deleted" label, because we don't yet know whether the record exists;
 *   - directory loaded + record found → render the human-readable email;
 *   - directory loaded + record missing → render "deleted admin <prefix>".
 */
function renderActor(
  adminId: string,
  admins: Map<string, AdminLite> | null,
  t: TFn,
): JSX.Element {
  const admin = admins?.get(adminId);
  if (admin) return <span className="font-medium text-fg">{admin.email}</span>;
  if (admins === null) {
    return <code className="font-mono text-fg-subtle">{adminId.slice(0, 8)}</code>;
  }
  return (
    <span className="flex items-baseline gap-1">
      <span className="italic">{t('audit.actor.deletedAdmin', { defaultValue: 'deleted admin' })}</span>
      <code className="font-mono text-fg-subtle">{adminId.slice(0, 8)}</code>
    </span>
  );
}

function renderTarget(
  tenantId: string,
  tenants: Map<string, TenantLite> | null,
  t: TFn,
): JSX.Element {
  const tenant = tenants?.get(tenantId);
  if (tenant) {
    return (
      <Link
        to={`/platform/tenants/${tenant.id}`}
        className="font-medium text-fg underline-offset-2 hover:text-brand hover:underline"
      >
        {tenant.name}
      </Link>
    );
  }
  if (tenants === null) {
    return <code className="font-mono text-fg-subtle">{tenantId.slice(0, 8)}</code>;
  }
  return (
    <span className="flex items-baseline gap-1">
      <span className="italic">{t('audit.actor.deletedTenant', { defaultValue: 'deleted tenant' })}</span>
      <code className="font-mono text-fg-subtle">{tenantId.slice(0, 8)}</code>
    </span>
  );
}

export function PlatformAuditPage(): JSX.Element {
  const { t, i18n } = useTranslation('platform');
  const [items, setItems] = useState<AuditEntry[] | null>(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [filters, setFilters] = useState({ adminId: '', tenantId: '', action: '' });
  const [error, setError] = useState<string | null>(null);
  const [admins, setAdmins] = useState<Map<string, AdminLite> | null>(null);
  const [tenants, setTenants] = useState<Map<string, TenantLite> | null>(null);

  // Audit entries store actor + target as UUIDs. Resolve them to email /
  // tenant-name once on mount so the log reads like history, not opaque
  // identifiers. Failures fall back to the truncated UUID — the audit page
  // stays useful even if the directory endpoints reject this admin.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [adminRes, tenantRes] = await Promise.all([
          platformApi().get<{ items: Array<{ id: string; email: string }> }>('/admins'),
          platformApi().get<{
            items: Array<{ tenant: { id: string; name: string; slug: string } }>;
          }>('/tenants', { params: { limit: 500 } }),
        ]);
        if (cancelled) return;
        setAdmins(new Map(adminRes.data.items.map((a) => [a.id, { id: a.id, email: a.email }])));
        setTenants(new Map(tenantRes.data.items.map((t) => [t.tenant.id, t.tenant])));
      } catch {
        // Silent fail — UUID prefixes still render as a fallback.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Pull `tenantId=...` out of the query string so the "View full audit log
  // for this tenant" link on the detail page can deep-link here.
  useEffect(() => {
    const url = new URL(window.location.href);
    const tid = url.searchParams.get('tenantId');
    if (tid) setFilters((f) => ({ ...f, tenantId: tid }));
  }, []);

  useEffect(() => {
    setOffset(0);
  }, [filters]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params: Record<string, string> = {
          limit: String(PAGE_SIZE),
          offset: String(offset),
        };
        if (filters.adminId.trim()) params.adminId = filters.adminId.trim();
        if (filters.tenantId.trim()) params.tenantId = filters.tenantId.trim();
        if (filters.action.trim()) params.action = filters.action.trim();
        const r = await platformApi().get<{ items: AuditEntry[]; total: number }>('/audit', {
          params,
        });
        if (!cancelled) {
          setItems(r.data.items);
          setTotal(r.data.total);
        }
      } catch (e) {
        if (!cancelled) setError(String((e as Error).message ?? e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filters, offset]);

  const activeFilterCount = useMemo(
    () =>
      [filters.adminId, filters.tenantId, filters.action].filter((v) => v.trim().length > 0).length,
    [filters],
  );

  const clearFilters = () => setFilters({ adminId: '', tenantId: '', action: '' });

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-warning/15 text-warning">
            <FileClock className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h1 className="text-2xl font-semibold">{t('audit.title')}</h1>
            <p className="mt-1 text-sm text-fg-muted">{t('audit.subtitle')}</p>
          </div>
        </div>
        {items && total > 0 && (
          <span className="text-xs text-fg-muted">
            {t('tenants.pagination.showing', {
              from: offset + 1,
              to: Math.min(offset + items.length, total),
              total,
            })}
          </span>
        )}
      </header>

      <Card className="p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <FormField id="f-admin" label={t('audit.filter.admin')}>
            <Input
              id="f-admin"
              value={filters.adminId}
              onChange={(e) => setFilters((f) => ({ ...f, adminId: e.target.value }))}
              placeholder="uuid"
              className="font-mono text-xs"
            />
          </FormField>
          <FormField id="f-tenant" label={t('audit.filter.tenant')}>
            <Input
              id="f-tenant"
              value={filters.tenantId}
              onChange={(e) => setFilters((f) => ({ ...f, tenantId: e.target.value }))}
              placeholder="uuid"
              className="font-mono text-xs"
            />
          </FormField>
          <FormField id="f-action" label={t('audit.filter.action')}>
            <Input
              id="f-action"
              value={filters.action}
              onChange={(e) => setFilters((f) => ({ ...f, action: e.target.value }))}
              placeholder="tenant.create"
              className="font-mono text-xs"
            />
          </FormField>
        </div>
        {activeFilterCount > 0 && (
          <div className="mt-3 flex items-center gap-2 text-xs">
            <span className="text-fg-muted">{t('audit.activeFilters', { count: activeFilterCount, defaultValue: '{{count}} active' })}</span>
            <ShButton variant="ghost" size="sm" onClick={clearFilters} className="h-7 px-2 text-xs">
              <X className="h-3.5 w-3.5" />
              {t('audit.clearFilters', { defaultValue: 'Clear' })}
            </ShButton>
          </div>
        )}
      </Card>

      {error && (
        <p
          className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger"
          role="alert"
        >
          {error}
        </p>
      )}

      {items === null ? (
        <Card className="p-6"><Skeleton className="h-48 w-full" /></Card>
      ) : items.length === 0 ? (
        activeFilterCount > 0 ? (
          <EmptyState
            title={t('audit.noResults')}
            action={
              <ShButton variant="outline" size="sm" onClick={clearFilters}>
                {t('audit.clearFilters')}
              </ShButton>
            }
          />
        ) : (
          <EmptyState title={t('audit.empty')} />
        )
      ) : (
        <Card className="p-6">
          <ol className="relative ms-3 border-s-2 border-border-subtle">
            {items.map((a) => {
              const meta = metaFor(a.action);
              const Icon = meta.icon;
              return (
                <li key={a.id} className="ms-6 pb-5 last:pb-0">
                  <span
                    className="absolute -start-[15px] mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-bg ring-2 ring-border-subtle"
                    aria-hidden
                  >
                    <Icon className="h-3 w-3 text-fg-muted" />
                  </span>
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Badge variant={meta.variant}>
                        {t(`audit.action.${a.action}`, { defaultValue: a.action })}
                      </Badge>
                      <code className="text-[0.65rem] text-fg-subtle">{a.action}</code>
                    </div>
                    <span className="flex items-center gap-1 text-xs text-fg-muted">
                      <Clock className="h-3 w-3" aria-hidden />
                      {formatDateTime(a.createdAt, { locale: i18n.language }, { dateStyle: 'medium', timeStyle: 'medium' })}
                    </span>
                  </div>
                  <p className="mt-1 flex flex-wrap items-baseline gap-x-1.5 text-xs text-fg-muted">
                    <span>{t('audit.actor.by', { defaultValue: 'by' })}</span>
                    {renderActor(a.platformAdminId, admins, t)}
                    {a.targetTenantId && (
                      <>
                        <span aria-hidden>·</span>
                        <span>{t('audit.actor.on', { defaultValue: 'on' })}</span>
                        {renderTarget(a.targetTenantId, tenants, t)}
                      </>
                    )}
                  </p>
                  {a.diff !== null && a.diff !== undefined && <AuditDiff diff={a.diff} />}
                </li>
              );
            })}
          </ol>
          {total > items.length && (
            <div className="mt-4 flex justify-end gap-2">
              <ShButton
                variant="outline"
                size="sm"
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                disabled={offset === 0}
              >
                {t('tenants.pagination.prev')}
              </ShButton>
              <ShButton
                variant="outline"
                size="sm"
                onClick={() => setOffset(offset + PAGE_SIZE)}
                disabled={offset + items.length >= total}
              >
                {t('tenants.pagination.next')}
              </ShButton>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
